import { useEffect, useState } from 'react';

export interface AudioPeaks {
  /** Normalized 0-1 amplitude per bar. */
  peaks: number[];
  /** Seconds. */
  duration: number;
}

const cache = new Map<string, AudioPeaks>();

type AudioContextCtor = typeof AudioContext;

async function computePeaks(src: string, bars: number): Promise<AudioPeaks> {
  const res = await fetch(src);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.arrayBuffer();
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  if (!Ctor) throw new Error('Web Audio not supported');
  const ctx = new Ctor();
  try {
    const buffer = await ctx.decodeAudioData(data);
    const channel = buffer.getChannelData(0);
    const size = Math.max(1, Math.floor(channel.length / bars));
    const raw: number[] = [];
    for (let i = 0; i < bars; i += 1) {
      let max = 0;
      const from = i * size;
      const to = Math.min(channel.length, from + size);
      for (let j = from; j < to; j += 1) {
        const v = Math.abs(channel[j]);
        if (v > max) max = v;
      }
      raw.push(max);
    }
    const top = Math.max(...raw, 0.0001);
    return { peaks: raw.map((v) => v / top), duration: buffer.duration };
  } finally {
    ctx.close().catch(() => undefined);
  }
}

/**
 * Decodes an audio file into waveform bars. Returns null while loading or if the
 * file can't be read (e.g. no CORS on the audio bucket) — the player then shows
 * a placeholder waveform but still plays through a normal <audio> element.
 */
export default function useAudioPeaks(src: string, bars = 72): AudioPeaks | null {
  const key = `${bars}|${src}`;
  const [result, setResult] = useState<AudioPeaks | null>(() => cache.get(key) ?? null);

  useEffect(() => {
    if (!src) {
      setResult(null);
      return undefined;
    }
    const cached = cache.get(key);
    if (cached) {
      setResult(cached);
      return undefined;
    }
    let cancelled = false;
    setResult(null);
    computePeaks(src, bars)
      .then((r) => {
        // Blob URLs are one-off recordings; don't keep them around.
        if (!src.startsWith('blob:')) cache.set(key, r);
        if (!cancelled) setResult(r);
      })
      .catch((err: unknown) => console.error('[school] Waveform unavailable', src, err));
    return () => {
      cancelled = true;
    };
  }, [src, bars, key]);

  return result;
}
